import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useLocalParticipant, useRemoteParticipants, useRoomContext } from '@livekit/components-react';
import { LocalVideoTrack, RoomEvent, RpcError, Track } from 'livekit-client';
import type { RemoteParticipant } from 'livekit-client';
import {
  LEVELS,
  SEND_RESOLUTIONS,
  getPrefs,
  recvLevelFor,
  sendCapFor,
  setPrefs,
  useQualityPrefs,
} from './quality';
import type { Level, OperatorCaps, QualityPrefs, SendResolution } from './quality';
import './quality.css';
import { verifiedSigned } from './signed';

/**
 * MN-11: the dock's quality menu. Each person chooses, for themselves:
 *
 *   SEND     the camera resolution they capture at, and an upload cap (the
 *            highest simulcast layer allowed out);
 *   RECEIVE  the highest layer they want of everyone, or of one person;
 *   STATS    whether every tile shows its real numbers (StatsReadout.tsx).
 *
 * Mount it in CallStage's `dockExtra`. It is also what APPLIES the choices to
 * the room, so it must stay mounted for the whole call — the dock hides itself
 * by fading, never by unmounting, so being in the dock is enough.
 *
 * Every receive choice is a CEILING, not a demand: LiveKit's SFU still drops a
 * layer when the link cannot carry it. That is what you want on a bad link, and
 * it is why the readout shows what actually arrives rather than echoing the
 * setting back.
 */

const LEVEL_LABEL: Record<Level, string> = { high: 'HI', med: 'MID', low: 'LO' };
const LEVEL_TITLE: Record<Level, string> = {
  high: 'Full quality',
  med: 'Medium layer (about half resolution)',
  low: 'Low layer (about a quarter resolution)',
};

/**
 * What each camera track was last ASKED to capture at. A fresh track was asked
 * for LiveKit's default, h720 (Call.tsx passes no capture options), which is
 * the store's default too — so at default prefs nothing is ever restarted.
 */
const requested = new WeakMap<LocalVideoTrack, SendResolution>();
const LIVEKIT_DEFAULT: SendResolution = '720p';

/** Applies send resolution and upload cap to the local camera. */
function useApplySend(prefs: QualityPrefs) {
  const { cameraTrack, isCameraEnabled } = useLocalParticipant();
  const track = cameraTrack?.track;

  useEffect(() => {
    if (!(track instanceof LocalVideoTrack) || !isCameraEnabled) return;
    let cancelled = false;
    void (async () => {
      // Compare against what was REQUESTED, never what the device delivered:
      // a 640x480 webcam asked for 720p settles below it every time, and
      // comparing against its output would reopen it on every join.
      if ((requested.get(track) ?? LIVEKIT_DEFAULT) !== prefs.sendRes) {
        requested.set(track, prefs.sendRes);
        try {
          // deviceId kept explicitly: restartTrack with options and no device
          // would reopen the DEFAULT camera, not the one chosen in the dock.
          // A mute/unmute later reuses these constraints, so this sticks.
          const deviceId = track.mediaStreamTrack.getSettings().deviceId;
          await track.restartTrack({ resolution: SEND_RESOLUTIONS[prefs.sendRes].resolution, deviceId });
        } catch (e) {
          console.warn('quality: could not change capture resolution', e);
        }
      }
      // After the restart, which recomputes the sender's encodings.
      if (!cancelled) track.setPublishingQuality(LEVELS[sendCapFor(prefs)]);
    })();
    return () => {
      cancelled = true;
    };
  }, [track, isCameraEnabled, prefs.sendRes, prefs.sendCap, prefs.op.sendCap]);
}

/** Applies the receive ceilings to every remote camera, now and as they arrive. */
function useApplyReceive(prefs: QualityPrefs, people: RemoteParticipant[]) {
  const room = useRoomContext();

  const apply = useCallback(() => {
    for (const p of room.remoteParticipants.values()) {
      const level = LEVELS[recvLevelFor(prefs, p.identity)];
      for (const pub of p.videoTrackPublications.values()) {
        // Cameras only. A screen share is read, not watched, and a person who
        // turns everyone down to LO on a weak link still needs the slide legible.
        if (pub.source === Track.Source.Camera) pub.setVideoQuality(level);
      }
    }
  }, [room, prefs]);

  useEffect(() => {
    apply();
    room.on(RoomEvent.TrackPublished, apply);
    room.on(RoomEvent.TrackSubscribed, apply);
    return () => {
      room.off(RoomEvent.TrackPublished, apply);
      room.off(RoomEvent.TrackSubscribed, apply);
    };
  }, [room, apply, people]);
}

/**
 * Answers `meetnixctl call ROOM tune` (MWE-9). LiveKit has no server API for a
 * subscriber's layer or a publisher's cap, so the portal asks THIS client over
 * RPC, and it applies an operator's caps on top of its user's own choice for
 * this call, then says what is in force.
 *
 * ONLY THE PORTAL MAY ASK, or any participant could turn a colleague's video
 * down. Who is calling cannot say: LiveKit delivers the server's own RPC under
 * a fresh random caller identity, no different from a participant not yet
 * announced (TestTuneRPCCallerAgainstARealSFU). So the request is signed with
 * a key the portal gave this browser alone with its join token, and anything
 * unsigned, wrongly signed or stale is refused.
 *
 * The caps last this call: cleared when the call view mounts and unmounts, and
 * a reload is a new join the portal no longer reports as tuned.
 */
function useOperatorTuning(tuneKey: string | undefined) {
  const room = useRoomContext();
  useEffect(() => {
    setPrefs({ op: {} });
    return () => setPrefs({ op: {} });
  }, []);
  useEffect(() => {
    if (!tuneKey) return; // a portal that predates tuning: nothing to answer
    const method = 'meetnix.tune';
    room.registerRpcMethod(method, async (data) => {
      const req = await verifiedTune(tuneKey, data.payload);
      if (!req) throw new RpcError(1403, 'not a tune request from the portal');
      const level = (v: string | undefined): Level | undefined =>
        v && v in LEVELS ? (v as Level) : undefined;
      const prev = getPrefs().op;
      const op: OperatorCaps = req.reset
        ? {}
        : {
            recv: level(req.max_layer) ?? prev.recv,
            sendCap: level(req.send_layer) ?? prev.sendCap,
            by: req.by || prev.by,
          };
      setPrefs({ op });
      return JSON.stringify({ max_layer: op.recv ?? '', send_layer: op.sendCap ?? '' });
    });
    return () => room.unregisterRpcMethod(method);
  }, [room, tuneKey]);
}

interface TuneRequest {
  max_layer?: string;
  send_layer?: string;
  reset?: boolean;
  by?: string;
  at?: number;
  kind?: string;
}

/** A tune request the portal signed for this browser (signed.ts); a granted
 *  control (kind "control", MWE-27) is not one. */
async function verifiedTune(key: string, payload: string): Promise<TuneRequest | null> {
  const req = await verifiedSigned<TuneRequest>(key, payload);
  return req && !req.kind ? req : null;
}

function Seg<T extends string>({
  value,
  options,
  label,
  onChange,
  titles,
}: {
  value: T | undefined;
  options: readonly T[];
  label: string;
  onChange: (v: T) => void;
  titles?: Partial<Record<T, string>>;
}) {
  return (
    <div className="mx-q-seg" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          role="radio"
          aria-checked={value === o}
          title={titles?.[o]}
          onClick={() => onChange(o)}
        >
          {(LEVEL_LABEL as Record<string, string>)[o] ?? o}
        </button>
      ))}
    </div>
  );
}

const SEND_OPTIONS = Object.keys(SEND_RESOLUTIONS) as SendResolution[];
const LEVEL_OPTIONS = Object.keys(LEVELS) as Level[];

export function QualityMenu({ tuneKey }: { tuneKey?: string }) {
  const prefs = useQualityPrefs();
  // Agents publish nothing to tune, and are nobody to list (MWE-1).
  const people = useRemoteParticipants().filter((p) => !p.isAgent);
  useApplySend(prefs);
  useApplyReceive(prefs, people);
  useOperatorTuning(tuneKey);

  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);
  const panelId = useId();

  // Close on Escape or a click anywhere else; a menu that has to be closed by
  // finding its button again is a menu that stays open over somebody's face.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const setFor = (identity: string, v: Level | 'all') => {
    const recvFor = { ...prefs.recvFor };
    if (v === 'all') delete recvFor[identity];
    else recvFor[identity] = v;
    setPrefs({ recvFor });
  };

  return (
    <div className="mx-q" ref={root}>
      <button
        type="button"
        className="mx-dock-btn"
        aria-label="Video quality and stats"
        title="Video quality and stats"
        aria-expanded={open}
        aria-controls={panelId}
        aria-pressed={prefs.stats}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="mx-q-glyph" aria-hidden="true">
          HD
        </span>
      </button>
      {open && (
        <div className="mx-q-panel" id={panelId} role="dialog" aria-label="Video quality">
          {(prefs.op.recv || prefs.op.sendCap) && (
            <p className="mx-q-op" role="status">
              {prefs.op.by || 'An operator'} capped your video for this call:{' '}
              {[
                prefs.op.recv && `receiving at most ${LEVEL_LABEL[prefs.op.recv]}`,
                prefs.op.sendCap && `sending at most ${LEVEL_LABEL[prefs.op.sendCap]}`,
              ]
                .filter(Boolean)
                .join(', ')}
              .
            </p>
          )}
          <div className="mx-q-head">
            <span>Send</span>
            <span className="mx-q-hint">your camera</span>
          </div>
          <div className="mx-q-row">
            <span>Capture</span>
            <Seg
              label="Camera resolution"
              value={prefs.sendRes}
              options={SEND_OPTIONS}
              onChange={(sendRes) => setPrefs({ sendRes })}
            />
          </div>
          <div className="mx-q-row">
            <span>Upload cap</span>
            <Seg
              label="Highest layer sent"
              value={prefs.sendCap}
              options={LEVEL_OPTIONS}
              titles={LEVEL_TITLE}
              onChange={(sendCap) => setPrefs({ sendCap })}
            />
          </div>

          <div className="mx-q-head">
            <span>Receive</span>
            <span className="mx-q-hint">ceiling; the link may give less</span>
          </div>
          <div className="mx-q-row">
            <span>Everyone</span>
            <Seg
              label="Receive quality for everyone"
              value={prefs.recv}
              options={LEVEL_OPTIONS}
              titles={LEVEL_TITLE}
              onChange={(recv) => setPrefs({ recv })}
            />
          </div>
          {people.map((p) => (
            <div className="mx-q-row" key={p.identity}>
              <span className="mx-q-name" title={p.identity}>
                {p.name || p.identity}
              </span>
              <Seg
                label={`Receive quality for ${p.name || p.identity}`}
                value={prefs.recvFor[p.identity] ?? 'all'}
                options={['all', ...LEVEL_OPTIONS] as const}
                titles={{ all: 'Same as everyone', ...LEVEL_TITLE }}
                onChange={(v) => setFor(p.identity, v)}
              />
            </div>
          ))}

          <div className="mx-q-row mx-q-stats">
            <span>Stats on tiles</span>
            <button
              type="button"
              role="switch"
              aria-checked={prefs.stats}
              className="mx-q-switch"
              onClick={() => setPrefs({ stats: !prefs.stats })}
            >
              {prefs.stats ? 'ON' : 'OFF'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
